import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* CSS Reset and Global Styles - Manufacturing Executive Standards */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
    height: 100%;
    /* Executive Display Optimization */
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }

  body {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize.base};
    font-weight: ${props => props.theme.typography.fontWeight.normal};
    line-height: ${props => props.theme.typography.lineHeight.base};
    color: ${props => props.theme.colors.text.primary};
    background-color: ${props => props.theme.colors.background.primary};
    
    /* Manufacturing Executive Font Rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    font-feature-settings: "kern" 1, "liga" 1;
    
    min-height: 100vh;
    position: relative;
  }

  /* Executive Typography Hierarchy - Manufacturing Psychology */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    line-height: ${props => props.theme.typography.lineHeight.tight};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.base};
  }

  /* Brand Name Typography - Baskerville Executive Gravitas */
  h1 {
    font-family: ${props => props.theme.typography.fontFamily.brand};
    font-size: ${props => props.theme.typography.fontSize['5xl']};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
    color: ${props => props.theme.colors.text.primary};
    
    /* Executive Brand Emphasis */
    @media (min-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.typography.fontSize['6xl']};
    }
    
    @media (min-width: ${props => props.theme.breakpoints.desktop}) {
      font-size: ${props => props.theme.typography.fontSize['7xl']};
    }
  }

  /* All Other Headings - Helvetica Neue Manufacturing Clarity */
  h2 {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize['4xl']};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
  }

  h3 {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    letter-spacing: ${props => props.theme.typography.letterSpacing.normal};
  }

  h4 {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
  }

  h5 {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }

  h6 {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    text-transform: uppercase;
    letter-spacing: ${props => props.theme.typography.letterSpacing.wide};
  }

  /* Executive Body Text - Manufacturing Readability */
  p {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    margin-bottom: ${props => props.theme.spacing.base};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    color: ${props => props.theme.colors.text.primary};
  }

  /* Large Body Text for Executive Impact */
  .text-large {
    font-size: ${props => props.theme.typography.fontSize.lg};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
  }

  /* Small Text for Supporting Information */
  .text-small {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.muted};
  }

  /* Executive Link Styling - Professional Interaction */
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.base};
    font-weight: ${props => props.theme.typography.fontWeight.medium};

    &:hover {
      color: ${props => props.theme.colors.interactive.hover};
      text-decoration: underline;
      text-decoration-color: ${props => props.theme.colors.secondary};
      text-underline-offset: 0.2em;
    }

    &:focus {
      outline: 2px solid ${props => props.theme.colors.interactive.focus};
      outline-offset: 2px;
      border-radius: ${props => props.theme.borderRadius.sm};
    }
  }

  /* Executive Button Base Styles */
  button {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    cursor: pointer;
    transition: all ${props => props.theme.transitions.base};
    border: none;
    border-radius: ${props => props.theme.borderRadius.base};
    
    &:focus {
      outline: 2px solid ${props => props.theme.colors.interactive.focus};
      outline-offset: 2px;
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      color: ${props => props.theme.colors.interactive.disabled};
    }
  }

  /* Form Elements - Executive Input Standards */
  input, textarea, select {
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize.base};
    font-weight: ${props => props.theme.typography.fontWeight.normal};
    color: ${props => props.theme.colors.text.primary};
    border: 1px solid ${props => props.theme.colors.border.medium};
    border-radius: ${props => props.theme.borderRadius.base};
    transition: border-color ${props => props.theme.transitions.base};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: ${props => props.theme.shadows.focus};
    }
  }

  /* Executive Image Optimization */
  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.base};
  }

  /* Manufacturing Executive Layout Classes */
  .container {
    max-width: ${props => props.theme.breakpoints.wide};
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing.content.base};

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      padding: 0 ${props => props.theme.spacing.content.narrow};
    }
  }

  .container-wide {
    max-width: ${props => props.theme.breakpoints.ultrawide};
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing.content.wide};

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      padding: 0 ${props => props.theme.spacing.content.base};
    }
  }

  /* Executive Text Alignment */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }

  /* Manufacturing Color Classes */
  .text-primary { color: ${props => props.theme.colors.text.primary}; }
  .text-secondary { color: ${props => props.theme.colors.text.secondary}; }
  .text-muted { color: ${props => props.theme.colors.text.muted}; }
  .text-light { color: ${props => props.theme.colors.text.light}; }
  .text-inverse { color: ${props => props.theme.colors.text.inverse}; }

  /* Executive Background Classes */
  .bg-primary { background-color: ${props => props.theme.colors.background.primary}; }
  .bg-secondary { background-color: ${props => props.theme.colors.background.secondary}; }
  .bg-dark { background-color: ${props => props.theme.colors.background.dark}; }
  .bg-muted { background-color: ${props => props.theme.colors.background.muted}; }

  /* Accessibility - Executive Standards */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Executive Focus Styles for Accessibility */
  *:focus-visible {
    outline: 2px solid ${props => props.theme.colors.interactive.focus};
    outline-offset: 2px;
    border-radius: ${props => props.theme.borderRadius.sm};
  }

  /* Manufacturing Executive Smooth Scrolling */
  html {
    scroll-behavior: smooth;
  }

  /* Executive Selection Styling */
  ::selection {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.text.inverse};
  }

  ::-moz-selection {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.text.inverse};
  }

  /* Executive Scrollbar Styling - Manufacturing Precision */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.secondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.secondary};
    border-radius: ${props => props.theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }

  /* Executive Print Styles - Manufacturing Standards */
  @media print {
    * {
      background: transparent !important;
      color: #000000 !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }

    body {
      font-size: 12pt;
      line-height: 1.4;
    }

    h1 {
      font-size: 24pt;
      font-family: "Times New Roman", serif;
    }

    h2, h3, h4, h5, h6 {
      font-family: "Arial", sans-serif;
    }

    a, a:visited {
      text-decoration: underline;
      color: #000000 !important;
    }

    a[href]:after {
      content: " (" attr(href) ")";
      font-size: 10pt;
    }

    abbr[title]:after {
      content: " (" attr(title) ")";
    }

    pre, blockquote {
      border: 1px solid #999999;
      page-break-inside: avoid;
      padding: 8pt;
    }

    thead {
      display: table-header-group;
    }

    tr, img {
      page-break-inside: avoid;
    }

    img {
      max-width: 100% !important;
      height: auto !important;
    }

    p, h2, h3 {
      orphans: 3;
      widows: 3;
    }

    h2, h3 {
      page-break-after: avoid;
    }

    /* Executive Header for Print */
    @page {
      margin: 1in;
    }
  }

  /* Executive Responsive Typography - Manufacturing Scalability */
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    html {
      font-size: 14px;
    }
    
    h1 {
      font-size: ${props => props.theme.typography.fontSize['4xl']};
    }
    
    h2 {
      font-size: ${props => props.theme.typography.fontSize['3xl']};
    }
    
    h3 {
      font-size: ${props => props.theme.typography.fontSize['2xl']};
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.ultrawide}) {
    html {
      font-size: 18px;
    }
  }

  /* Executive Animation Preferences - Manufacturing Respect */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Executive Dark Mode Support - Manufacturing Flexibility */
  @media (prefers-color-scheme: dark) {
    /* Note: Dark mode implementation would go here if needed */
    /* Currently optimized for light mode executive presentation */
  }
`;